<template>
  <div>
    <loadingCube v-if="loading"/>
    <table v-else class="ns-table">
      <thead>
      <tr>
        <th><div class="capsule"><span>Rank</span></div></th>
        <th><div class="capsule"><span>User</span></div></th>
        <th v-for="k in columnKeys" :key="k" class="rotate">
          <div class="capsule"><span>{{ columnLabels[k] }}</span></div>
        </th>
        <th class="rotate">
          <div class="capsule"><span>Grand Total</span></div>
        </th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="row in rows" :key="row.user_id">
        <td>{{ row.rank }}</td>
        <td>
          <router-link
              v-if="$store.state.auth.me.permissions.includes('sunset_note_stats_restricted')"
              :to="{
              name: 'reports_notestats_user_totals',
              params: {
                userid: row.user_id,
                type: effectiveQuery.side,
                start: formatDate(effectiveQuery.start),
                end:   formatDate(effectiveQuery.end),
              },
              query: (query || {})
            }"
            target="_blank"
          >
            {{ row.user_name }}
          </router-link>
          <span v-else>
            {{ row.user_name }}
          </span>
        </td>
        <td v-for="k in columnKeys" :key="k">{{ toInt(row[k]) }}</td>
        <td>{{ toInt(row.grand_total) }}</td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
@use "../../scss/shared" as *;
</style>

<script>
import loadingCube from 'loaders/record_loader';
import { formatDate } from '../shared/utils';

export default {
  name: 'sunset_note_stats_leaderboard',
  components: { loadingCube },
  props: {
    // from <sunset-note-stats-query-bar v-slot="{ params, meta, query }">
    params: { type: Object, required: true }, // { side,start,end,windowed,mask,match,notMatch,includeOther,limit,apiBaseUrl,... }
    meta:   { type: Object, required: true },
    query:  { type: Object, default: () => ({}) }, // optional extra query map
  },
  data() {
    return {
      loading: true,
      rows: [],
      refreshTimer: null,
      defaultApiBaseUrl: '/api/v1/',
    };
  },
  computed: {
    apiBase() {
      const base = this.params?.apiBaseUrl || this.defaultApiBaseUrl;
      return String(base).replace(/\/+$/, '');
    },

    // merge params + query (query wins)
    effectiveQuery() {
      const p = this.params || {};
      const q = this.query || {};
      const merged = { ...p, ...q };

      return {
        side:  String((merged.side || 'combined')).toLowerCase(),
        mask:  String((merged.mask || 'all')),
        start: merged.start || '',
        end:   merged.end   || '',
        match: merged.match ?? '',
        not:   (merged.not ?? merged.notMatch ?? ''),
        includeOther: this.parseBool(merged.includeOther ?? true, true),
        windowed:     this.parseBool(merged.windowed ?? p.windowed ?? false, false),
        limit: (merged.limit != null && merged.limit !== '') ? merged.limit : null,
        // pass-throughs (harmless if API ignores on this endpoint)
        user: merged.user ?? merged.userId ?? merged.user_id,
        perPage: merged.perPage,
        page: merged.page,
      };
    },

    isWindowed() { return this.effectiveQuery.windowed; },

    // windows + note-type order/labels from backend
    windowsOrder() { return Array.isArray(this.meta?.windows) ? this.meta.windows : []; },
    windowLabels() { return this.meta?.window_labels || {}; },
    noteTypeLabels() { return this.meta?.note_types?.labels || {}; },
    noteTypeOrder() {
      return Array.isArray(this.meta?.note_types?.idents) ? this.meta.note_types.idents : [];
    },

    // derive present dynamic columns (respect backend ordering, append extras)
    columnKeys() {
      const present = new Set();
      for (const r of this.rows) {
        Object.keys(r).forEach(k => {
          if (k === 'rank' || k === 'user_id' || k === 'user_name' || k === 'grand_total') return;
          present.add(k);
        });
      }
      if (!present.size) return [];

      if (this.isWindowed) {
        const ordered = [];
        for (const ident of this.noteTypeOrder) {
          for (const w of this.windowsOrder) {
            const key = `${ident}__${w}`;
            if (present.has(key)) ordered.push(key);
          }
        }
        const extras = Array.from(present).filter(k => !ordered.includes(k)).sort();
        return ordered.concat(extras);
      }

      const ordered = this.noteTypeOrder.filter(k => present.has(k));
      const extras = Array.from(present).filter(k => !ordered.includes(k)).sort();
      return ordered.concat(extras);
    },

    columnLabels() {
      const map = {};
      for (const k of this.columnKeys) {
        if (this.isWindowed) {
          const [ident, win] = k.split('__');
          map[k] = `${this.noteTypeLabels[ident] || ident} — ${this.windowLabels[win] || win}`;
        } else {
          map[k] = this.noteTypeLabels[k] || k;
        }
      }
      return map;
    },

    requestUrl() {
      const q = this.effectiveQuery;
      const usp = new URLSearchParams();
      usp.set('side', q.side);
      usp.set('mask', q.mask);
      if (q.start) usp.set('start', q.start);
      if (q.end)   usp.set('end', q.end);
      usp.set('includeOther', q.includeOther ? '1' : '0');
      if (q.windowed) usp.set('windowed', '1');
      if (q.match)    usp.set('match', q.match);
      if (q.not)      usp.set('not', q.not);
      if (q.limit != null) usp.set('limit', String(q.limit));
      // pass-throughs (safe if server ignores)
      if (q.user != null)    usp.set('user', String(q.user));
      if (q.perPage != null) usp.set('perPage', String(q.perPage));
      if (q.page != null)    usp.set('page', String(q.page));

      return `${this.apiBase}/sunset/reports/note-stats/leaderboard?${usp.toString()}`;
    },
  },
  watch: {
    params: { handler() { this.scheduleFetch(); }, deep: true },
    query:  { handler() { this.scheduleFetch(); }, deep: true },
    // meta only affects labels/order - no refetch needed
  },
  async mounted() {
    await this.fetchLeaderboard();
  },
  beforeDestroy() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  },
  methods: {
    formatDate,
    toInt(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; },
    parseBool(v, fallback = false) {
      if (typeof v === 'boolean') return v;
      const s = String(v ?? '').toLowerCase();
      if (['1','true','yes','on'].includes(s)) return true;
      if (['0','false','no','off'].includes(s)) return false;
      return fallback;
    },
    scheduleFetch() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => { this.fetchLeaderboard(); }, 120);
    },
    async fetchLeaderboard() {
      const { start, end } = this.effectiveQuery;
      this.loading = true;
      try {
        if (!start || !end) {
          this.rows = [];
        } else {
          const res = await axios.get(this.requestUrl);
          const payload = res && res.data ? res.data : { data: [] };
          this.rows = Array.isArray(payload.data) ? payload.data : [];
        }
      } catch {
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
